import type { JSX } from 'react'

import { Button } from '../design-system/button.tsx'
import { Notice } from '../design-system/notice.tsx'

export interface SignInScreenProps {
  onSignIn: () => void
  busy?: boolean
  error?: string | null
  /**
   * Whether this environment has an Identity Platform tenant to sign in
   * against. False until EI-268 wires one in.
   */
  provisioned?: boolean
  /**
   * The address a developer machine signs in as, or null in a deploy.
   *
   * Set by `scripts/dev.sh` and read through `isLocalAuth`. It names the
   * person on the control, because local mode chooses nobody: the environment
   * has already decided who this is.
   */
  localViewer?: string | null
}

/**
 * Signed-out sign-in screen. Google SSO is the only authentication path.
 *
 * There is no email/password path, no magic link and no other provider — the
 * same rule super-ui holds. Adding one is a ticket, not a prop.
 *
 * When the environment has no tenant the control is withheld rather than
 * disabled-with-a-tooltip: a button that cannot ever work invites the reader
 * to keep pressing it, and the popup it would open fails with an Identity
 * Platform code that reads as a broken deployment.
 *
 * **A developer machine gets a different control, not a second provider.**
 * `localhost` is not an allow-listed origin for the super tenant, so the
 * Google popup fails there with `auth/unauthorized-domain`. Local mode signs
 * in against no tenant at all, so the screen names the session it starts
 * rather than offering a chooser that cannot work.
 *
 * @param props - Sign-in screen props.
 * @param props.onSignIn - Starts the sign-in this environment supports.
 * @param props.busy - True while sign-in is in flight.
 * @param props.error - Inline failure; omitted when null.
 * @param props.provisioned - Whether a tenant exists to sign in against.
 * @param props.localViewer - The address local mode signs in as.
 * @returns The sign-in control, and an error banner when needed.
 */
export function SignInScreen({
  onSignIn,
  busy = false,
  error = null,
  provisioned = true,
  localViewer = null
}: SignInScreenProps): JSX.Element {
  // Asked before `provisioned`: local mode signs in against no tenant, so the
  // unprovisioned notice would otherwise withhold the one control that works
  // here.
  if (localViewer !== null) {
    return (
      <>
        {error ? (
          <Notice tone="danger" data-testid="sign-in-error">
            {error}
          </Notice>
        ) : null}
        <Button
          data-testid="sign-in-local"
          className="w-full"
          aria-busy={busy}
          disabled={busy}
          onClick={onSignIn}
        >
          {busy ? 'Signing in…' : `Sign in as ${localViewer}`}
        </Button>
      </>
    )
  }

  if (!provisioned) {
    return (
      <Notice tone="warning" data-testid="sign-in-unprovisioned">
        {
          'No Identity Platform tenant is configured in this environment, so nobody can sign in. EI-268 tracks wiring one in.'
        }
      </Notice>
    )
  }

  return (
    <>
      {error ? (
        <Notice tone="danger" data-testid="sign-in-error">
          {error}
        </Notice>
      ) : null}
      <Button
        data-testid="sign-in-google"
        className="w-full"
        aria-busy={busy}
        disabled={busy}
        onClick={onSignIn}
      >
        {busy ? 'Signing in…' : 'Sign in with Google'}
      </Button>
    </>
  )
}
